import { defineStore } from 'pinia';
import { conversationStateTypes, messageTypes } from '@/utils/assistant';
import { AssistantService } from '@/services/assistant';

import { useEventBus } from '@vueuse/core';
import { actionEditorEvents } from '@/utils/actionEditor';

import _ from 'lodash';

import { useIntentsStore } from '@/stores/intents';
import { useBotsStore } from '@/stores/bots';
import { useContentStore } from '@/stores/content';

/** One entry in the assistant chat history */
export interface AssistantChatItem {
  /** Who the message is from: one of `messageTypes` (user | assistant | system) */
  type: string;
  /** Plain text of the message (user input or assistant output) */
  text?: string;
  /** i18n translation key to render instead of plain text */
  t?: string;
  /** For user messages: position of this user message among all user messages */
  userInputIndex?: number;
  /** For user messages: whether this is the most recent user input */
  isLastUserInput?: boolean;
}

/** A canned suggestion prompt shown to the user */
export interface AssistantSuggestion {
  /** Suggestion text (German copy, inserted into the chat input) */
  text: string;
}

/** State of the assistant module (AI content assistant chat) */
export interface AssistantState {
  /** Current conversation state: one of `conversationStateTypes` (new/open/running/completed/canceled/error) */
  state: string;
  /** Backend conversation ID of the running conversation, or `null` if none */
  conversationId: string | null;
  /** Chat history rendered in the assistant panel */
  chat: AssistantChatItem[];
  /** Whether the assistant is busy (shows loading indicator) */
  loading: boolean;
  /** Whether the assistant panel is open */
  isAssistantOpen: boolean;
  /** Whether the assistant produced unsaved content changes */
  isAssistantDirty: boolean;
  /** Whether the assistant info view is shown */
  isAssistantInfo: boolean;
  /** Intent ID the assistant is currently working on, or `null` */
  intentId: string | null;
  /** Canned suggestion prompts */
  suggestions: AssistantSuggestion[];
  /** Content drafts backup: {[botId]: {[intent]: {[boxId]: box}} */
  drafts: Record<string, Record<string, any>>;
  /** Index of the next "hold the line" filler message */
  holdTheLineIndex: number;
  /** Timeout handle for the next "hold the line" message (`any`: browser/node typing mismatch, may be null) */
  holdTheLineTimeout: any;
  /** How many conversations were completed in this session (changes the greeting) */
  completedCounter: number;
  /** Abort controller for the in-flight assistant request, or `null` */
  abortController: AbortController | null;
}

/**
 * Pinia conversion of legacy vuex module `src/store/modules/assistant.ts` (namespace
 * `assistant`). Persisted to `localStorage` (full state) — matches the legacy `vuexLocal`
 * reducer's `assistant: state.assistant` entry in `src/store/index.ts`.
 *
 * Collisions & renames (see
 * `/private/tmp/claude-501/-Users-haslien-Documents-GitHub-moin-hub/23091b27-f9d9-4b85-91e6-ff6cd1c12710/scratchpad/pinia-migration/RENAMES.md`):
 * - State-prop/getter identity-passthrough collisions (getter renamed `<name>Getter`, per
 *   DESIGN.md's collision rule): `chat` -> `chatGetter`, `state` -> `stateGetter`,
 *   `loading` -> `loadingGetter`, `isAssistantOpen` -> `isAssistantOpenGetter`,
 *   `isAssistantDirty` -> `isAssistantDirtyGetter`, `isAssistantInfo` -> `isAssistantInfoGetter`.
 *   Pinia would otherwise have a state prop and a getter fighting over the same store-instance key
 *   — the renamed getter is a no-op passthrough consumers could get from the state prop directly,
 *   kept only so nothing that referenced the old getter name breaks in one shot.
 * - `suggestions` getter also collided with the `suggestions` state prop, but its body is NOT an
 *   identity passthrough — it returns a fresh hardcoded array of the four canned suggestions
 *   regardless of the current (settable) `state.suggestions` value. This looks like a legacy
 *   copy/paste artifact (the getter duplicates the state's initial value verbatim instead of
 *   reading it), but per the "keep intentionally-dead/quirky code, don't fix behavior" rule it is
 *   carried over unchanged, just renamed to `suggestionsGetter` to resolve the naming collision.
 * - Single legacy mutation `setValue` had no matching action name — converts 1:1 to a same-named
 *   action `setValue`.
 * - No action/mutation same-name merges existed in this module (only one mutation total, and it
 *   didn't share a name with any action).
 *
 * Cross-namespace access, resolved via sibling composables called INSIDE action/getter bodies:
 * - `rootGetters['intents/currentIntent']` -> `useIntentsStore().currentIntentGetter` (the
 *   `intents` module itself renamed its identity-passthrough `currentIntent` getter to
 *   `currentIntentGetter` for the same collision reason described above).
 * - `rootGetters['bots/currentBotId']` -> `useBotsStore().currentBotId` (no rename there).
 * - `dispatch('content/setContentDraftIntent', payload, { root: true })` ->
 *   `useContentStore().setContentDraftIntent(payload)` (no rename there).
 */
export const useAssistantStore = defineStore('assistant', {
  state: (): AssistantState => ({
    state: conversationStateTypes.new,
    conversationId: null,
    chat: [],
    loading: true,
    isAssistantOpen: false,
    isAssistantDirty: false,
    isAssistantInfo: false,
    intentId: null,
    suggestions: [
      {
        text: 'Worum geht es in der Antwort?',
      },
      {
        text: 'Mach aus Links / E-Mails / Telefonnummern Buttons.',
      },
      {
        text: 'Erstelle aus den Bildern ein Kartenmenü.',
      },
      {
        text: 'Erstelle aus dem Text eine passende Antwort. Nutze Multiple Choice um die Antwort besser zu strukturieren.',
      },
    ],
    // {[botId]: {[intent]: {[boxId]: box}}
    drafts: {},
    holdTheLineIndex: 0,
    holdTheLineTimeout: null,
    completedCounter: 0,
    abortController: null,
  }),
  getters: {
    /** Renamed from legacy getter `chat` — identity passthrough colliding with the `chat` state prop. */
    chatGetter: (state: AssistantState) => state.chat,
    /** Renamed from legacy getter `state` — identity passthrough colliding with the `state` state prop. */
    stateGetter: (state: AssistantState) => state.state,
    /** Renamed from legacy getter `loading` — identity passthrough colliding with the `loading` state prop. */
    loadingGetter: (state: AssistantState) => state.loading,
    /** Renamed from legacy getter `isAssistantOpen` — identity passthrough colliding with the `isAssistantOpen` state prop. */
    isAssistantOpenGetter: (state: AssistantState) => state.isAssistantOpen,
    /** Renamed from legacy getter `isAssistantDirty` — identity passthrough colliding with the `isAssistantDirty` state prop. */
    isAssistantDirtyGetter: (state: AssistantState) => state.isAssistantDirty,
    /** Renamed from legacy getter `isAssistantInfo` — identity passthrough colliding with the `isAssistantInfo` state prop. */
    isAssistantInfoGetter: (state: AssistantState) => state.isAssistantInfo,
    /**
     * Renamed from legacy getter `suggestions` — collides with the `suggestions` state prop. Note
     * this is NOT an identity passthrough: it always returns this fresh hardcoded list, ignoring
     * whatever `state.suggestions` currently holds. Kept verbatim (quirky legacy behavior, not a
     * bug we get to fix here).
     */
    suggestionsGetter: (): AssistantSuggestion[] => [
      {
        text: 'Worum geht es in der Antwort?',
      },
      {
        text: 'Mach aus Links / E-Mails / Telefonnummern Buttons.',
      },
      {
        text: 'Erstelle aus den Bildern ein Kartenmenü.',
      },
      {
        text: 'Erstelle aus dem Text eine passende Antwort. Nutze Multiple Choice um die Antwort besser zu strukturieren.',
      },
    ],
  },
  actions: {
    async init() {
      this.loading = true;
      // `currentIntentGetter` is typed `Intent | false`; legacy accessed `.intent` on it under
      // an untyped (`any`) rootGetters context, so `false?.intent` just evaluated to `undefined`
      // at runtime. Cast preserves that exact behavior without narrowing away the `false` branch.
      const intentId = (useIntentsStore().currentIntentGetter as any)?.intent;

      if (this.intentId !== intentId) {
        this.intentId = intentId;
        this.resetAssistant();
      }
      if (this.state === conversationStateTypes.new) {
        this.startAssistant();
      }
      await new Promise((resolve) => setTimeout(resolve, 500));
      this.loading = false;
    },

    async resetAssistant() {
      this.state = conversationStateTypes.new;
      this.chat = [];
      this.loading = false;
      this.isAssistantDirty = false;
      this.isAssistantInfo = false;
      this.drafts = {};
      this.conversationId = null;
      this.holdTheLineIndex = 0;
    },

    async startAssistant() {
      this.loading = true;

      await new Promise((resolve) => setTimeout(resolve, 500));

      let greeting = 'moin';

      if (this.completedCounter === 1) {
        greeting = 'moin2';
      } else if (this.completedCounter > 1) {
        greeting = 'moin3';
      }

      this.chat = [
        {
          type: messageTypes.assistant,
          t: `assistant.chatAssistant.${greeting}`,
        },
      ];
      this.loading = false;
    },
    sendMessage({message, intent, content, channel}: { message: string; intent: string; content: any; channel?: string }) {
      this.isAssistantInfo = false;
      switch (this.state) {
        case conversationStateTypes.new:
          this.handleNewConversation({ message, intent, content, channel });
          break;
        case conversationStateTypes.open:
          // Legacy dispatched `handleOpenConversation` with the same `{message, intent, content}`
          // payload as the `new` branch, even though that action only destructures `message` (the
          // extra `intent`/`content` props were always ignored there). Cast preserves the
          // over-wide call verbatim instead of dropping the properties.
          this.handleOpenConversation({ message, intent, content } as any);
          break;
      }
    },

    async handleNewConversation({ message, intent, content, channel }: { message: string; intent: string; content: any; channel?: string }) {

      this.conversationId = null;
      this.loading = true;
      const uniqueBotId = useBotsStore().currentBotId;
      const contents = _.cloneDeep(content);

      if (!uniqueBotId || !message || !intent || !contents) {
        console.error('handleNewConversation: something is missing');
        return;
      }

      const chat = [...this.chat];
      // Reset other chat items

      this.chat.forEach((item: AssistantChatItem) => {
        if (item.type === messageTypes.user) {
          item.isLastUserInput = false;
        }
      });

      chat.push({
        type: messageTypes.user,
        text: message,
        userInputIndex: 0,
        isLastUserInput: true,
      });

      this.chat = chat;

      this.sendHoldTheLineMessage({delay: 8000, intent});

      this.state = conversationStateTypes.running;
      this.abortController = new AbortController();
      const { conversation, error } = await AssistantService.startConversation({
        message,
        uniqueBotId,
        intent,
        contents,
        signal: this.abortController.signal,
        channel
      });

      // get only the conversation ID

      if (error || !conversation) {
        this.handleChatError();
        return false;
      }

      if (!conversation) {
        this.loading = false;
        this.state = conversationStateTypes.new;
        return;
      }

      this.conversationId = conversation?.conversationId;
      this.state = conversation?.state;
    },
    async handleOpenConversation({ message }: { message: string }) {
      console.log('handleOpenConversation');
      this.loading = true;
      const uniqueBotId = useBotsStore().currentBotId;
      let stateBefore = null;

      this.resetHoldTheLine();

      // Add user message to chat
      // userInputIndex: position of the user message in the chat
      const userChatItems = this.chat.filter((item: AssistantChatItem) => item.type === 'user');
      const userInputIndex = userChatItems.length;

      this.chat.forEach((item: AssistantChatItem) => {
        if (item.type === messageTypes.user) {
          item.isLastUserInput = false;
        }
      });

      this.chat = [
        ...this.chat,
        {
          type: messageTypes.user,
          text: message,
          userInputIndex,
          isLastUserInput: true,
        },
      ];


      stateBefore = this.state;
      this.state = conversationStateTypes.running;
      this.abortController = new AbortController();
      const conversationId = this.conversationId;
      this.conversationId = null;

      // NOTE: legacy call site passed a bare number here instead of the `{delay, intent}` shape
      // `sendHoldTheLineMessage` expects — kept verbatim (pre-existing quirk, not fixed here).
      this.sendHoldTheLineMessage(2000 as any);

      const { error, conversation } = await AssistantService.continueConversation(
        {
          message,
          // `conversationId`/`uniqueBotId` are `string | null` here (legacy ran this under an
          // untyped rootGetters/state context with no null-check either); non-null assertions
          // keep the exact legacy call shape instead of adding a new guard.
          conversationId: conversationId!,
          uniqueBotId: uniqueBotId!,
          signal: this.abortController.signal,
        }
      );

      this.conversationId = conversation?.conversationId;

    },

    async handleConversationCompleted(
      conversation: any
    ) {
      this.loading = true;

      this.resetHoldTheLine();

      const intent = conversation.intent;
      const uniqueBotId = conversation.botId;
      // "content" aka. contentStore.js
      // const oldDraftIntent = rootGetters['content/getContentBoxes'](
      //   uniqueBotId,
      //   intent
      // );

      // if (oldDraftIntent) {
      //   console.log('oldDraftIntent', oldDraftIntent);
      //   dispatch('setDraft', {
      //     botId: uniqueBotId,
      //     intent,
      //     boxes: oldDraftIntent,
      //   });
      // }

      // const boxIds = oldDraftIntent.map((box) => box.box);

      // // TODO 😭
      // dispatch(
      //   'content/deleteContentDrafts',
      //   {
      //     botId: uniqueBotId,
      //     intentId: intent,
      //     boxIds,
      //   },
      //   { root: true }
      // );

      // await dispatch(
      //   'content/setContentDraftIntent',
      //   {
      //     botId: uniqueBotId,
      //     intent: intent,
      //     boxes: conversation.content,
      //     channel: currentIntent.channel,
      //   },
      //   { root: true }
      // );

      //this.isAssistantDirty = true;
      // this.completedCounter += 1;
      const contentUpdatedEmitter = useEventBus(actionEditorEvents.contentUpdated);
      contentUpdatedEmitter.emit({
        content: conversation.content,
        event: actionEditorEvents.contentOverwritten,
      });

      this.chat = [
        ...this.chat,
        {
          type: messageTypes.system,
          t: 'assistant.chatSystem.suggestionPasted',
        },
      ];

      this.chat = [
        ...this.chat,
        {
          type: messageTypes.assistant,
          text: conversation.output,
        },
      ];

      this.state = conversation.state;
      this.loading = false;
    },

    async handleConversationOpen(conversation: any) {

      this.resetHoldTheLine();

      this.chat = [
        ...this.chat,
        {
          type: messageTypes.assistant,
          text: conversation.output,
        },
      ];
      this.state = conversation.state;
      this.loading = false;
    },

    async handleConversationCanceled(conversation: any) {
      this.loading = true;

      this.resetHoldTheLine();

      this.chat = [
        ...this.chat,
        {
          type: messageTypes.system,
          t: 'assistant.chatSystem.suggestionCanceled',
        },
      ];

      this.chat = [
        ...this.chat,
        {
          type: messageTypes.assistant,
          text: conversation.output,
        },
      ];

      this.state = conversationStateTypes.canceled;
      this.loading = false;
    },

    async handleChatError() {
      console.log('assistant.handleChatError');

      this.resetHoldTheLine();

      this.chat = [
        ...this.chat,
        {
          type: messageTypes.system,
          t: 'assistant.chatSystem.error',
        },
        {
          type: messageTypes.assistant,
          t: 'assistant.chatAssistant.error',
        },
      ];
      this.state = conversationStateTypes.error;
      this.loading = false;
      return false;
    },

    async cancelRequest() {
      this.state = conversationStateTypes.new;
      this.loading = true;

      this.resetHoldTheLine();
      if (this.abortController?.abort) {
        this.abortController.abort();
      }

      this.chat = [
        {
          type: messageTypes.system,
          t: 'assistant.chatSystem.requestCaceled',
        },
        {
          type: messageTypes.assistant,
          t: 'assistant.chatAssistant.afterRequestCanceled',
        },
      ];

      // TODO Chat reset
      // Show Btn neu versuchen
      this.state = conversationStateTypes.new;
      this.loading = false;
    },

    async resetDraft({ withMessage }: { withMessage: boolean }) {
      console.log('assistant.resetDraft');

      this.resetHoldTheLine();

      this.loading = true;
      this.isAssistantDirty = false;
      this.state = conversationStateTypes.new;
      const currentIntent: any = useIntentsStore().currentIntentGetter;

      const boxes =
        this.drafts[currentIntent.botId] &&
        this.drafts[currentIntent.botId][currentIntent.intent]
          ? _.cloneDeep(this.drafts[currentIntent.botId][currentIntent.intent])
          : [];

      useContentStore().setContentDraftIntent({
        botId: currentIntent.botId,
        intent: currentIntent.intent,
        boxes,
        channel: currentIntent.channel,
      });

      if (withMessage) {
        await new Promise((resolve) => setTimeout(resolve, 1000));

        this.chat = [
          {
            type: messageTypes.system,
            t: 'assistant.chatSystem.suggestionReseted',
          },
          {
            type: messageTypes.assistant,
            t: 'assistant.chatAssistant.afterCanceled',
          },
        ];
      }

      this.loading = false;
      return true;
    },

    async acceptDraft() {
      console.log('assistant.acceptDraft');
      this.chat = [
        {
          type: messageTypes.system,
          t: 'assistant.chatSystem.suggestionAccepted',
        },
      ];
      this.state = conversationStateTypes.new;
      this.isAssistantDirty = false;
      await new Promise((resolve) => setTimeout(resolve, 200));
      this.isAssistantOpen = false;
    },

    setDraft({ botId, intent, boxes }: { botId: string; intent: string; boxes: any }) {
      console.log('assistant.setDraft');
      if (!this.drafts[botId]) {
        this.drafts[botId] = {};
      }
      this.drafts[botId][intent] = { ...boxes };
    },

    async startChangeInput(item: AssistantChatItem) {
      console.log('assistant.startChangeInput');

      const itemIndex = this.chat.findIndex(
        (i: AssistantChatItem) =>
          i.type === messageTypes.user && i.userInputIndex === item.userInputIndex
      );

      let newChat = this.chat.slice(0, itemIndex);

      if (item.userInputIndex === 0) {
        await this.resetDraft({ withMessage: false });
      } else {
        // TODO user input change from other index
      }

      await new Promise((resolve) => setTimeout(resolve, 500));

      newChat.push({
        type: messageTypes.system,
        t: 'assistant.chatSystem.suggestionReseted',
      });

      newChat.push({
        type: messageTypes.assistant,
        t: 'assistant.chatAssistant.afterStartChange',
      });

      this.chat = newChat;
      this.state = conversationStateTypes.new;
      this.loading = false;
    },

    async sendRequestAgain() {
      this.loading = true;

      // Delete chat after last user input
      const itemIndex = this.chat.findIndex((i: AssistantChatItem) => i.isLastUserInput);

      let newChat = this.chat.slice(0, itemIndex - 1);

      const message = this.chat[itemIndex]?.text || null;

      if (!message) {
        console.error('sendRequestAgain: no message found');
        this.state = conversationStateTypes.new;
        this.init();
        return;
      }
      this.chat = newChat;
      this.handleOpenConversation({ message });
    },

    async sendHoldTheLineMessage({delay = 8000, intent}: { delay?: number; intent?: string }) {

      if (this.conversationId) {
        const uniqueBotId = useBotsStore().currentBotId;
        // check if conversations still running
        const { error, conversation } = await AssistantService.getConversation(
          {
            conversationId: this.conversationId,
            // `uniqueBotId` is `string | null` (legacy passed it unchecked from an untyped
            // rootGetters context); non-null assertion keeps the same unguarded call.
            uniqueBotId: uniqueBotId!,
          }
        );

        if (conversation && conversation.state !== 'running') {
          this.state = conversation?.state;

          if (conversation.state === conversationStateTypes.completed) {
            this.handleConversationCompleted(conversation);
            return;
          }

          if (conversation.state === conversationStateTypes.canceled) {
            this.handleConversationCanceled(conversation);
            return;
          }

          if (conversation.state === conversationStateTypes.open) {
            this.handleConversationOpen(conversation);
            return;
          }

          this.loading = false;
          return;
        }
      }


      clearTimeout(this.holdTheLineTimeout);
      this.holdTheLineTimeout = setTimeout(() => {
        const index = this.holdTheLineIndex;
        this.chat = [
          ...this.chat,
          {
            type: messageTypes.assistant,
            t: `assistant.holdTheLine[${index}]`,
          },
        ];
        this.holdTheLineIndex = index + 1;
        this.sendHoldTheLineMessage({delay, intent});
      }, delay);
    },

    resetHoldTheLine() {
      console.log('assistant.resetHoldTheLine');
      clearTimeout(this.holdTheLineTimeout);
      this.holdTheLineIndex = 0;
    },

    /**
     * Converted from legacy mutation `setValue` (no matching legacy action existed). Generic
     * escape hatch to set an arbitrary top-level state key by name.
     */
    setValue({ key, value }: { key: string; value: any }) {
      (this as any)[key] = value;
    },
  },
  vuexPersist: {
    local: true,
  },
});
